import { HttpErrorResponse } from '@angular/common/http';
import { Component, HostBinding, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, distinctUntilChanged, firstValueFrom, map, Observable, Subscription } from 'rxjs';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FieldExpression, LuceneExpression, NodeExpression, term,
	parse as luceneToParse, toString as luceneToString, OperatorExpression } from 'lucene';
import { Location, NgIf, NgFor } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { GuaranteeRequestService, REQUEST_FIELDS } from '../../services/guarantee-request.service';
import { Guarantee } from '../../models/guarantee';
import { GuaranteeRequestAttachment } from '../../models/guarantee-request-attachment';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { UNIT_OPTIONS } from '../../../shared/models/list';
import { User } from '../../../shared/models/user';
import { ListFilter } from '../../../shared/models/filter';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { State } from '../../../order/models/localization-state';
import { ListFilterService } from '../../../shared/services/list-filter.service';
import { UserService } from '../../../shared/services/user.service';
import { GuaranteeStatus } from '../../models/guarantee-constants';
import { getGuaranteeRequestStatusTr } from '../../utils/guarantee.utils';
import { TranslationService } from '../../../shared/services/translation.service';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { DialogDeleteDraftComponent, RESPONSE_CONFIRM } from
	'../utils/dialog-delete-draft/dialog-delete-draft.component';
import { SnackbarDeleteDraftComponent } from
	'../../../claim/components/utils/snackbar-delete-draft/snackbar-delete-draft.component';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { LoadingDialogComponent } from '../entry/step-4/loading-dialog.component';
import {
	DialogRequestCertificateComponent,
	DialogRequestCertificateData
} from '../utils/dialog-request-certificate/dialog-request-certificate.component';

@Component({
	selector: 'copo-guarantee-list',
	templateUrl: './guarantee-list.component.html',
	styleUrl: './guarantee-list.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		CdkScrollable,
		BackButtonComponent,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,

		MatButtonModule,
		MatIconModule,
		VirtualScrollerDirective,
		NgFor,
		RouterLink,

		MatMenuModule,
		LoadingSpinnerComponent,
		DatePipe,
		SafeUrlPipe
	]
})
export class GuaranteeListComponent implements OnDestroy {
	@HostBinding('class.passthrough') componentClass = true;

	readonly UNIT_OPTIONS = UNIT_OPTIONS;
	readonly UNDERSCORE = UNDERSCORE;

	user: User;
	submitting = false;
	requestingCertificate = false;
	certificateRequestRefresh = 0;

	searchTextSubscription: Subscription;
	globalSearchTextSubscription?: Subscription;
	deleteDraftSubscription?: Subscription;
	searchFocus = false;
	searchTextControl = new FormControl('', { nonNullable: true });
	filters = '';
	currentFilters?: ListFilter;
	datacenterType: DatacenterType;
	mergedFilter?: LuceneExpression;
	state: State;

	constructor(
		private readonly userService: UserService,
		private readonly guaranteeRequestService: GuaranteeRequestService,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly listFilterService: ListFilterService,
		private readonly translateService: TranslationService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
		location: Location
	) {
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.state = <State> location.getState();
		this.searchTextSubscription = this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(value => {
			this.setFilters(value);
		});
		const initialSearchText = this.route.snapshot.queryParamMap.get('search')
			?? this.route.snapshot.paramMap.get('query');
		if (initialSearchText !== null) {
			this.searchTextControl.setValue(initialSearchText);
		}
		this.currentFilters = this.getAppliedFilters();
		let criterionFilterNode;
		if (this.listFilterService.getLuceneFromAllFilters(this.currentFilters)) {
			criterionFilterNode = this.listFilterService.getLuceneFromAllFilters(this.currentFilters);
		}
		if (criterionFilterNode !== undefined) {
			this.mergedFilter = criterionFilterNode;
		}

		const isStatus = this.currentFilters.filterState !== null
			? this.currentFilters.filterState.filter(state => state.id === 'status').length : 0;

		if (isStatus === 0) {
			const notDraftNode: OperatorExpression = {
				start: 'NOT',
				left: {
					field: 'status',
					term: 'Draft',
					quoted: true
				}
			};
			this.mergedFilter = this.mergedFilter
				? {
					left: this.mergedFilter,
					operator: 'AND',
					right: notDraftNode,
					parenthesized: false
				}
				: notDraftNode;
		}

		this.setFilters(this.searchTextControl.value);
		this.user = this.userService.user;
	}

	get dataId() {
		return `guarantee-${ this.filters }-${ this.certificateRequestRefresh }`;
	}

	ngOnDestroy() {
		this.searchTextSubscription.unsubscribe();
		this.globalSearchTextSubscription?.unsubscribe();
		this.deleteDraftSubscription?.unsubscribe();
	}

	isDraft(request: Guarantee) {
		return request.status === GuaranteeStatus.DRAFT;
	}

	isOpen(request: Guarantee) {
		return request.status === GuaranteeStatus.OPEN;
	}

	isOther(request: Guarantee) {
		return request.status === GuaranteeStatus.LAUNCH_ANALYSIS;
	}

	isSatisfied(request: Guarantee) {
		return request.status === GuaranteeStatus.SATISFIED;
	}

	isCertificateRequested(request: Guarantee) {
		return request.status === GuaranteeStatus.CERTIFICATE_REQUESTED;
	}

	isCertificateIssued(request: Guarantee) {
		return request.status === GuaranteeStatus.CERTIFICATE_ISSUED;
	}

	isRejected(request: Guarantee) {
		return request.status === GuaranteeStatus.REJECTED_BY_SUPLIER;
	}

	isCancelled(request: Guarantee) {
		return request.status === GuaranteeStatus.CANCELLED;
	}

	isUnknown(request: Guarantee) {
		return !this.isDraft(request)
			&& !this.isOpen(request)
			&& !this.isOther(request)
			&& !this.isSatisfied(request)
			&& !this.isCertificateRequested(request)
			&& !this.isCertificateIssued(request)
			&& !this.isRejected(request)
			&& !this.isCancelled(request);
	}

	getGuaranteeRequestStatusTr(request: Guarantee) {
		return getGuaranteeRequestStatusTr(this.translateService, request.status);
	}

	async editDraft(draftId: string) {
		this.submitting = true;
		await this.router.navigate(
			['/guarantee-requests/edit', draftId, 'project-characteristics'],
			{ replaceUrl: true }
		);
	}

	evtDeleteDraft(draft: Guarantee) {
		const path = '/guarantee-requests/list';
		const sub = this.dialog.open<DialogDeleteDraftComponent, number>(
			DialogDeleteDraftComponent,
			{ panelClass: 'copo-claim-dialog' }
		);
		sub.componentInstance.draftName = draft.name;
		this.deleteDraftSubscription = sub.afterClosed()
			.subscribe(async response => {
				if (response === RESPONSE_CONFIRM && draft.id !== undefined) {
					try {
						await this.guaranteeRequestService.deleteGuaranteeRequest(draft.id);
						this.snackBar.openFromComponent(
							SnackbarDeleteDraftComponent,
							{
								duration: 5000,
								panelClass: ['copo-draft-snackbar']
							}
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					} catch (err) {
						console.error('unable to delete draft', err);
						this.snackBar.open(
							this.translateService.getTR('error.deleting.draft'),
							undefined,
							{ duration: 5000 }
						);
						await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
					}
				}
			});
	}

	async requestCertificate(request: Guarantee) {
		if (this.requestingCertificate || !request.id || !this.isSatisfied(request)) {
			return;
		}

		const confirmation = this.dialog.open<DialogRequestCertificateComponent, DialogRequestCertificateData, boolean>(
			DialogRequestCertificateComponent,
			{
				data: { guaranteeName: request.name },
				panelClass: 'copo-claim-dialog',
				autoFocus: false
			}
		);
		const confirmed = await firstValueFrom(confirmation.afterClosed());
		if (confirmed !== true) {
			return;
		}

		this.requestingCertificate = true;
		const loadingDialog = this.dialog.open<LoadingDialogComponent>(
			LoadingDialogComponent,
			{ disableClose: true, minWidth: '256px', autoFocus: false }
		);
		let requestSucceeded = false;
		try {
			await this.guaranteeRequestService.requestCertificate(request.id);
			this.certificateRequestRefresh++;
			requestSucceeded = true;
		} catch (err: unknown) {
			console.error('unable to request guarantee certificate', err);
			if (err instanceof HttpErrorResponse && err.status === 409) {
				this.certificateRequestRefresh++;
			}
			this.snackBar.open(
				this.translateService.getTR(
					err instanceof HttpErrorResponse && err.status === 409
						? 'guarantee.certificate.request.unavailable'
						: 'guarantee.certificate.request.failed'
				),
				undefined,
				{
					duration: 5000,
					panelClass: ['copo-guarantee-error-snackbar']
				}
			);
		} finally {
			loadingDialog.close();
			this.requestingCertificate = false;
		}
		if (requestSucceeded) {
			this.snackBar.open(
				this.translateService.getTR('guarantee.certificate.request.success'),
				undefined,
				{ duration: 5000 }
			);
		}
	}

	setFilters(fullTextStr: string) {
		this.filters = fullTextStr !== ''
			? this.mergedFilter
				? this.createQuickSearchAndFilterQuery(
					this.createQuickSearchOrFilterQuery(fullTextStr),
					this.mergedFilter
				)
				: this.createQuickSearchOrFilterQuery(fullTextStr)
			: this.mergedFilter ? luceneToString(this.mergedFilter) : '';
	}

	createQuickSearchAndFilterQuery(quicksearchQuery: string, filterQuery: LuceneExpression) {
		const globalNode: NodeExpression = {
			left: luceneToParse(quicksearchQuery),
			operator: 'AND',
			right: filterQuery,
			parenthesized: false
		};

		return luceneToString(globalNode);
	}

	createQuickSearchOrFilterQuery(textSearch: string) {
		const nameOrGuaranteeIdNode: NodeExpression = {
			left: this.createQuickSearchFilterOnName(textSearch),
			operator: 'OR',
			right: this.createQuickSearchFilterOnGuaranteeId(textSearch),
			parenthesized: true
		};
		const globalNode: NodeExpression | FieldExpression =
		textSearch.length === 15 || textSearch.length === 18 ? {
			left: this.createQuickSearchFilterOnId(textSearch),
			operator: 'OR',
			right: nameOrGuaranteeIdNode,
			parenthesized: true
		} : nameOrGuaranteeIdNode;
		return luceneToString(globalNode);
	}

	createQuickSearchFilterOnId(textSearch: string) {
		const idField: FieldExpression = {
			field: 'id',
			term: term.escape(textSearch),
			quoted: true
		};

		return idField;
	}

	createQuickSearchFilterOnName(textSearch: string) {
		const filterText = `*${ term.escape(textSearch) }*`;
		const nameField: FieldExpression = {
			field: 'name',
			term: filterText,
			quoted: false
		};

		return nameField;
	}

	createQuickSearchFilterOnGuaranteeId(textSearch: string) {
		const filterText = `*${ term.escape(textSearch) }*`;
		const guaranteeIdField: FieldExpression = {
			field: 'guaranteeId',
			term: filterText,
			quoted: false
		};

		return guaranteeIdField;
	}

	async goToFilter() {
		if (!this.currentFilters) {
			return;
		}
		this.listFilterService.filterToEdit = {
			filterState: this.currentFilters,
			datacenterType: this.currentFilters.datacenterType
		};
		await this.router.navigate(
			['filter'],
			{ queryParams: { type: this.datacenterType }, relativeTo: this.route }
		);
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Guarantee[]>) => void) {
		provideData(
			this.guaranteeRequestService.getRequests(
				REQUEST_FIELDS,
				this.filters,
				index,
				limit,
				[new OrderBy('requestDate', OrderByDirection.DESC)],
				false,
				false
			).pipe(map(res => res.results))
		);
	}

	async duplicateDetails(guaranteeId: string) {
		this.submitting = true;
		const concatGuaranteeName = ` ${ this.translateService.getTR('guarantee.name.copy') }`;
		const guaranteeObj = await firstValueFrom(
			this.guaranteeRequestService.getGuaranteeById(guaranteeId)
		);
		this.guaranteeRequestService.guarantee = new Guarantee();
		Object.assign(this.guaranteeRequestService.guarantee, guaranteeObj);
		this.guaranteeRequestService.guarantee.name = this.guaranteeRequestService
			.guarantee.name.concat(concatGuaranteeName);
		this.guaranteeRequestService.guarantee.status = GuaranteeStatus.DRAFT;
		this.guaranteeRequestService.guarantee.id = undefined;
		this.guaranteeRequestService.guarantee.requestDate = undefined;
		await this.router.navigate(['../new/project-characteristics'], { relativeTo: this.route });
		this.submitting = false;
	}

	getLongType(appType: string) {
		switch (appType) {
		case 'Magnelis-Solar':
			return ` ${ this.translateService.getTR('guarantee.magnelissolar.long') }`;
		case 'Magnelis-Building':
			return ` ${ this.translateService.getTR('guarantee.magnelisbuilding.long') }`;
		case 'Magnelis-Other application':
			return ` ${ this.translateService.getTR('guarantee.magnelisothers.long') }`;
		case 'Organic Coating':
			return ` ${ this.translateService.getTR('guarantee.organiccoating.long') }`;
		}
		return '';
	}

	getCertificateUrl(request: Guarantee, certificate: GuaranteeRequestAttachment) {
		if (!request.id || !certificate.chronicleId) {
			return '';
		}
		return this.guaranteeRequestService.getDownloadFile(request.id, certificate.chronicleId);
	}

	private getAppliedFilters() {
		const listFilter: ListFilter = {
			filterState: this.state.filter ?? [],
			datacenterType: this.datacenterType
		};
		const appliedFilter = this.state.filter
			? listFilter
			: this.listFilterService.getAppliedFilters(this.datacenterType) ?? listFilter;
		this.listFilterService.setCurrentFilter(appliedFilter);
		return appliedFilter;
	}
}
